import { Callback } from "game-common/models"
import { Container, Graphics, Rectangle, Text } from "pixi.js"
import { TextRenderer } from "../text/text_renderer"

// The "you are not alone" chip: a small pill in the top-right corner, beside the map toggle, carrying
// the number of OTHER players logged into the server. It is present only while there is somebody else
// to count -- a solo camp has no use for it, and the corner is better empty than occupied by a zero.
//
// Deliberately built off the map chip's own plate (dark translucent, thin light rim, glyph + label) so
// the pair reads as one row of controls rather than two unrelated badges.
//
// Because it appears on its own -- nothing the player did puts it there -- it announces itself with an
// orange FLASH on arrival. See flash()/animate(): a couple of hot pulses that decay back to the
// resting plate, which is enough to catch the eye without turning into a permanent blinking light.
const PAD_X = 6
const PAD_Y = 4
const CORNER = 5
const GLYPH = "👥"
const GLYPH_SIZE = 11
const GAP = 4

const PANEL_COLOR = 0x05070a
const PANEL_ALPHA = 0.59
const PANEL_ALPHA_HOVER = 0.82
const BORDER_ALPHA = 0.36
const BORDER_ALPHA_HOVER = 0.75
const LABEL_COLOR = 0xcfd8dc
const LABEL_COLOR_HOVER = 0xffffff

// The arrival flash. The same hot orange the highlighted roster edge-arrow uses, so "look here" means
// one colour across the HUD.
export const FLASH_COLOR = 0xff8a1f
export const FLASH_MS = 1400
const FLASH_PULSES = 2.5

// How hot the flash is `t` ms into it: 1 at its peak, 0 once it is over. A decaying pulse train --
// the pulses keep the chip moving (a single fade reads as a rendering hiccup) and the decay is what
// guarantees it ends. Exported so the shape can be asserted without a renderer.
export const flashIntensity = (remainingMs: number): number => {
    if (!(remainingMs > 0)) {
        return 0
    }
    const remaining = Math.min(1, remainingMs / FLASH_MS)
    // Pulses run on the ELAPSED fraction so the train starts hot at its first peak rather than at
    // zero, and `remaining` (squared, so the tail goes quietly) is the envelope.
    const elapsed = 1 - remaining
    const pulse = Math.abs(Math.cos(elapsed * Math.PI * FLASH_PULSES))
    return pulse * remaining * remaining
}

// Blend `from` toward `to` by `amount` (0..1), per channel. Used rather than pixi's tint because the
// plate is a filled Graphics whose fill AND rim have to move together.
export const blendColor = (from: number, to: number, amount: number): number => {
    const a = Math.max(0, Math.min(1, amount))
    const mix = (shift: number) => {
        const f = (from >> shift) & 0xff
        const t = (to >> shift) & 0xff
        return Math.round(f + (t - f) * a) & 0xff
    }
    return (mix(16) << 16) | (mix(8) << 8) | mix(0)
}

export class EmberPlayersChip extends Container {
    private plate = new Graphics()
    private glyph: Text
    private label: Text
    private count = 0
    private hovered = false
    private flashMs = 0

    constructor(onClick: Callback) {
        super()

        // No tint on the emoji -- it carries its own colours.
        this.glyph = new TextRenderer().fontSize(GLYPH_SIZE).render(GLYPH) as Text
        this.label = new TextRenderer().size("tiny").render("0") as Text
        this.layout()

        this.addChild(this.plate, this.glyph, this.label)
        this.draw()

        this.interactive = true
        this.cursor = "pointer"
        this.on("mouseover", () => {
            this.hovered = true
            this.draw()
        })
        this.on("mouseout", () => {
            this.hovered = false
            this.draw()
        })
        this.on("click", onClick)
        this.on("tap", onClick)

        this.visible = false
    }

    // How many others there are. Going from none to some is what makes the chip appear, and an
    // appearance always flashes -- including one caused by walking into a camp while somebody else is
    // already online, which is exactly the case worth announcing.
    update = (count: number) => {
        const next = Math.max(0, Math.floor(count || 0))
        const appearing = next > 0 && this.count < 1
        this.count = next
        this.label.text = String(next)
        this.layout()
        this.visible = next > 0
        if (appearing) {
            this.flash()
        }
        this.draw()
    }

    flash = () => {
        this.flashMs = FLASH_MS
    }

    isFlashing = (): boolean => this.flashMs > 0

    // Drain the flash. Only repaints while one is running, so a chip that has settled costs nothing
    // per frame.
    animate = (dtMs: number) => {
        if (this.flashMs <= 0) {
            return
        }
        this.flashMs = Math.max(0, this.flashMs - dtMs)
        this.draw()
    }

    // Both sit on a shared centre line, so the glyph and the count don't stagger. Re-run whenever the
    // count's width changes (1 digit to 2), which also moves the hit area.
    private layout = () => {
        this.glyph.x = PAD_X
        this.label.x = PAD_X + this.glyph.width + GAP
        const h = Math.max(this.glyph.height, this.label.height)
        this.glyph.y = PAD_Y + (h - this.glyph.height) / 2
        this.label.y = PAD_Y + (h - this.label.height) / 2
        // An explicit hit area a couple of px proud of the plate: the chip is small, and it is the one
        // way into the player list.
        this.hitArea = new Rectangle(-3, -3, this.getWidth() + 6, this.getHeight() + 6)
    }

    private draw = () => {
        const hot = flashIntensity(this.flashMs)
        this.plate.clear()
        this.plate.beginFill(
            blendColor(PANEL_COLOR, FLASH_COLOR, hot * 0.75),
            (this.hovered ? PANEL_ALPHA_HOVER : PANEL_ALPHA) + hot * (1 - PANEL_ALPHA),
        )
        this.plate.lineStyle(
            1 + hot,
            blendColor(0xffffff, FLASH_COLOR, hot),
            Math.min(1, (this.hovered ? BORDER_ALPHA_HOVER : BORDER_ALPHA) + hot),
        )
        this.plate.drawRoundedRect(0, 0, this.getWidth(), this.getHeight(), CORNER)
        this.plate.endFill()
        this.label.tint = blendColor(this.hovered ? LABEL_COLOR_HOVER : LABEL_COLOR, 0xffffff, hot)
    }

    // Measured off the content rather than getBounds(), so the plate can be drawn to the size the
    // caller is about to position against without a chicken-and-egg on bounds.
    getWidth = (): number => PAD_X * 2 + this.glyph.width + GAP + this.label.width
    getHeight = (): number => PAD_Y * 2 + Math.max(this.glyph.height, this.label.height)
}
